import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { set, get } from 'lodash';
import { useTranslation } from 'react-i18next';
import { usePlanningActualState } from './Context/index';
import { checkArray, roundOff } from '../../Components/helper';
import * as planningActualAction from './store/PlanningActual.action';
import TotalTable from './TotalTable';
import useCalculationHooks from './Hooks/useCalculationHooks';
import { toasterMsg } from '../../Components/Toaster/Toaster';

function List({ categoryYear, addEditAccess }) {
  const dispatch = useDispatch();
  const reducer = useSelector((reducerstate) => reducerstate);
  const { PlanningActualData } = reducer;
  const { planningActualReducer } = PlanningActualData;
  const { t } = useTranslation('common');
  const { calculateColumnsValues, checkLimitOfHundred } = useCalculationHooks();
  const {
    planningActual: {
      overAllColumnValues: { isPlannedSales, isActualSales },
      modifiedData,
      staticFields,
      validateColumns,
      eachColumnsTotals,
    },
    setPlanningActual,
  } = usePlanningActualState();

  const [state, setState] = useState({
    year: null,
    season: null,
    category: [],
  });

  useEffect(() => {
    if (categoryYear) {
      setState((prevState) => ({
        ...prevState,
        year: categoryYear?.year,
        season: categoryYear?.seasonText?.name,
      }));
    }
  }, [categoryYear]);

  useEffect(() => {
    if (checkArray(modifiedData?.category)) {
      setState((prevState) => ({
        ...prevState,
        category: modifiedData?.category,
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        category: [],
      }));
    }
  }, [modifiedData?.category]);

  const handleInput = async (value, iCat, iSub, keyName) => {
    const copyState = modifiedData;
    let ovalAllTotal = null;
    set(copyState, `category[${iCat}].subcategory[${iSub}][${keyName}]`, value);
    ovalAllTotal = await calculateColumnsValues(copyState);
    const catId = get(copyState, `category[${iCat}].categoryId._id`);
    const subCatId = get(copyState, `category[${iCat}].subcategory[${iSub}].subcategoryId._id`);
    let subCatValue = copyState.category[iCat].subcategory[iSub];
    subCatValue = {
      ...subCatValue,
      subcategoryId: subCatValue.subcategoryId._id,
    };
    const objParam = {
      season: categoryYear?.season,
      year: categoryYear?.year,
      categoryId: catId,
      subcategoryId: subCatId,
      subCategoryValues: subCatValue,
      overallTotals: ovalAllTotal.overallTotal,
    };
    if (modifiedData?._id) {
      objParam._id = modifiedData?._id;
    }
    modifiedData.overallTotals = ovalAllTotal.overallTotal;
    const res = await dispatch(planningActualAction.autoSaveData(modifiedData));

    if (res?.code) {
      setPlanningActual((prevState) => ({
        ...prevState,
        modifiedData: { ...modifiedData, _id: res?.data?._id },
      }));
    }
  };

  const handleChange = async (value, iCat, iSub, keyName) => {
    const copyState = modifiedData;
    let copyValidateColumns = validateColumns;
    const getOldValue = get(planningActualReducer, `category[${iCat}].subcategory[${iSub}][${keyName}]`);
    set(copyState, `category[${iCat}].subcategory[${iSub}][${keyName}]`, value);
    if (['plannedMix', 'actualMix'].includes(keyName)) {
      const limitStatus = await checkLimitOfHundred(copyState, keyName);
      if (limitStatus) {
        set(copyState, `category[${iCat}].subcategory[${iSub}][${keyName}]`, getOldValue);
        set(copyValidateColumns, `category[${iCat}].subcategory[${iSub}][${keyName}]`, 'Exceeding limit of 100');
        toasterMsg('You can`t exceed the limit of 100 ', 'danger');
      } else if (Object.keys(copyValidateColumns).length > 0) {
        copyValidateColumns = {};
      }
      setData(copyValidateColumns, copyState);
    } else if (['plannedMarkdownPer', 'actualMarkdownPer', 'plannedBuyingMargin', 'actualBuyingMargin'].includes(keyName)) {
      if (Number(value) >= 0 && Number(value) <= 100) {
        set(copyState, `category[${iCat}].subcategory[${iSub}][${keyName}]`, value);
        setData(copyValidateColumns, copyState);
      }
    } else {
      setPlanningActual((prevState) => ({
        ...prevState,
        modifiedData: copyState,
      }));
    }
  };

  const checkNumberValue = (value) => {
    return value?.replace(/[^\d]/g, '');
  };

  const returnZeroIfNan = (value) => {
    return Number.isNaN(value) ? 0 : value;
  };

  const setInputOrReadOnly = (staticEle, subCatEle, iCat, iSubCat) => {
    if (isPlannedSales && staticEle?.keyName === 'plannedSales' && staticEle.type === 'input') {
      return (
        <input
          value={returnZeroIfNan(subCatEle[staticEle.keyName])}
          onChange={(e) => {
            const caret = e.target.selectionStart;
            const element = e.target;
            const inputValue = checkNumberValue(e.target.value);

            handleChange(inputValue, iCat, iSubCat, staticEle.keyName);
            window.requestAnimationFrame(() => {
              element.selectionStart = caret;
              element.selectionEnd = caret;
            });
          }}
          onBlur={(e) => handleInput(e.target.value, iCat, iSubCat, staticEle.keyName)}
          onKeyDown={(e) => {
            if (e.keyCode === 110) e.preventDefault();
          }}
          disabled={!addEditAccess}
          className="form-control bordered-input"
          type="text"
        />
      );
    }
    if (!isPlannedSales && staticEle?.keyName === 'plannedSales' && staticEle.type === 'input') {
      return returnZeroIfNan(subCatEle[staticEle.keyName]);
    }
    if (isPlannedSales && staticEle?.keyName === 'plannedMix' && staticEle.type === 'input') {
      return returnZeroIfNan(subCatEle[staticEle.keyName]);
    }

    if (isActualSales && staticEle?.keyName === 'actualSales' && staticEle.type === 'input') {
      return (
        <input
          value={returnZeroIfNan(subCatEle?.[staticEle?.keyName])}
          onChange={(e) => {
            const caret = e.target.selectionStart;
            const element = e.target;
            const inputValue = checkNumberValue(e.target.value);

            handleChange(inputValue, iCat, iSubCat, staticEle.keyName);
            window.requestAnimationFrame(() => {
              element.selectionStart = caret;
              element.selectionEnd = caret;
            });
          }}
          type="text"
          onBlur={(e) => handleInput(e.target.value, iCat, iSubCat, staticEle.keyName)}
          onKeyDown={(e) => {
            if (e.keyCode === 110) e.preventDefault();
          }}
          disabled={!addEditAccess}
          className="form-control bordered-input"
        />
      );
    }
    if (!isActualSales && staticEle?.keyName === 'actualSales' && staticEle.type === 'input') {
      return returnZeroIfNan(subCatEle?.[staticEle?.keyName]);
    }
    if (isActualSales && staticEle?.keyName === 'actualMix' && staticEle.type === 'input') {
      return returnZeroIfNan(subCatEle?.[staticEle?.keyName]);
    }
    if (staticEle.type === 'input') {
      return (
        <input
          value={returnZeroIfNan(subCatEle?.[staticEle?.keyName])}
          onChange={(e) => {
            const caret = e.target.selectionStart;
            const element = e.target;
            const inputValue = checkNumberValue(e.target.value);

            handleChange(inputValue, iCat, iSubCat, staticEle.keyName);
            window.requestAnimationFrame(() => {
              element.selectionStart = caret;
              element.selectionEnd = caret;
            });
          }}
          type="text"
          onBlur={(e) => handleInput(e.target.value, iCat, iSubCat, staticEle.keyName)}
          onKeyDown={(e) => {
            if (e.keyCode === 189 && ['plannedMarkdownPer', 'actualMarkdownPer'].includes(staticEle.keyName)) e.preventDefault();
          }}
          disabled={!addEditAccess}
          className="form-control bordered-input"
        />
      );
    }
    return returnZeroIfNan(subCatEle?.[staticEle?.keyName]);
  };

  const setData = (copyValidateColumns, copyState) => {
    setPlanningActual((prevState) => ({
      ...prevState,
      validateColumns: copyValidateColumns,
      modifiedData: copyState,
    }));
  };

  const valueColumnHandler = (headerText, valuesOfColumn) => {
    return headerText.keyName in valuesOfColumn ? valuesOfColumn[headerText.keyName] : 0;
  };
  return (
    <fieldset disabled={!addEditAccess}>
      <div className="overflow-auto pb-3 px-3" style={{ height: 'calc(100vh - 230px)' }}>
        {modifiedData !== null && <TotalTable addEditAccess={addEditAccess} />}

        {checkArray(state?.category) ? (
          state?.category.map((categoryData, iCat) => {
            const eachColumnValues = eachColumnsTotals[categoryData?.categoryId?._id];
            return (
              <div key={categoryData?.categoryId?._id} className="d-flex">
                <table className="table custom-table layout-fixed  planning-actual-tbl font-13 mr-3">
                  <thead>
                    <tr>
                      <th>{categoryData?.categoryId?.name}</th>
                      {staticFields.map((headerText, i) => {
                        return (
                          headerText.display && (
                            <th key={i} className="w-150px">
                              {headerText.name}
                            </th>
                          )
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {categoryData?.subcategory.map((subCatEle, iSubCat) => {
                      return (
                        <tr key={iSubCat}>
                          <td>{subCatEle?.subcategoryId?.name}</td>
                          {staticFields.map((staticEle, iStatic) => {
                            const columnsValidation = get(validateColumns, `category[${iCat}].subcategory[${iSubCat}][${staticEle.keyName}]`, '');
                            return (
                              staticEle.display && (
                                <td className={staticEle?.bgColor} key={iStatic}>
                                  {setInputOrReadOnly(staticEle, subCatEle, iCat, iSubCat)}
                                  <span className="error-message">{columnsValidation}</span>
                                </td>
                              )
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>

                  <tfoot>
                    <tr>
                      <th>{t('PLANNING_ACTUAL.PLANING_LIST.TOTAL')}</th>
                      {staticFields.map((headerText, i) => {
                        const valuesOfColumn = eachColumnValues?.find((val) => val[headerText.keyName] >= 0 || val[headerText.keyName] < 0);
                        const values = valuesOfColumn ? valueColumnHandler(headerText, valuesOfColumn) : 0;
                        return (
                          headerText.display && (
                            <th key={i} className="w-150px">
                              {roundOff(values, 2)}
                            </th>
                          )
                        );
                      })}
                    </tr>
                  </tfoot>
                </table>
              </div>
            );
          })
        ) : (
          <div>
            <p className="text-center mt-3">{t('PLANNING_ACTUAL.PLANING_LIST.NO_RECORDS_FOUND')}</p>
          </div>
        )}
      </div>
    </fieldset>
  );
}

export default List;
